// pages/secure/PolicyCenterPage.js
// The Policy Center: staff read and acknowledge the brokerage's governing
// documents (68 IAC 1450.700 written company policy, ICA template, manuals).
// Managing broker gets full versioned CRUD — all in pop-ups to keep the page clean.

import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { useAuth } from '../../hooks/useAuth';
import Modal from '../../components/secure/Modal';

const TYPE_LABELS = {
    office_policy_manual: 'Office Policy Manual',
    independent_contractor_agreement: 'Independent Contractor Agreement',
    employee_manual: 'Employee Manual',
    agent_onboarding_manual: 'Agent Onboarding Manual',
    commission_schedule: 'Commission Schedule',
    ai_use_policy: 'AI Use Policy',
    other: 'Other',
};
const STATUS_BADGE = {
    active: 'bg-green-100 text-green-700',
    draft: 'bg-amber-100 text-amber-700',
    archived: 'bg-slate-100 text-slate-500',
};
const EMPTY_DRAFT = { doc_type: 'office_policy_manual', title: '', content: '' };

const PolicyCenterPage = () => {
    const apiClient = useApiClient();
    const { user } = useAuth();
    const isMB = !!user?.isManagingBroker;

    const [docs, setDocs] = useState([]);
    const [pending, setPending] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    // Modals
    const [reading, setReading] = useState(null);        // full doc being read
    const [signature, setSignature] = useState('');
    const [editing, setEditing] = useState(null);        // draft form {policy_id?, doc_type, title, content}
    const [acksFor, setAcksFor] = useState(null);        // {doc, rows}
    const [isSaving, setIsSaving] = useState(false);

    const load = useCallback(() => {
        setIsLoading(true);
        Promise.all([apiClient.get('/api/policies'), apiClient.get('/api/policies/pending/me')])
            .then(([d, p]) => { setDocs(d); setPending(p); })
            .catch(err => setError(err.message || 'Failed to load the Policy Center.'))
            .finally(() => setIsLoading(false));
    }, [apiClient]);

    useEffect(() => { load(); }, [load]);

    const openDoc = async (policyId) => {
        try {
            setSignature('');
            setReading(await apiClient.get(`/api/policies/${policyId}`));
        } catch (err) { setError(err.message); }
    };

    const acknowledge = async () => {
        setIsSaving(true);
        try {
            await apiClient.post(`/api/policies/${reading.policy_id}/acknowledge`, { signature_name: signature });
            setReading(null);
            load();
        } catch (err) { setError(err.message); }
        finally { setIsSaving(false); }
    };

    const saveDraft = async () => {
        setIsSaving(true);
        setError('');
        try {
            if (editing.policy_id) {
                await apiClient.put(`/api/policies/${editing.policy_id}`, editing);
            } else {
                await apiClient.post('/api/policies', editing);
            }
            setEditing(null);
            load();
        } catch (err) { setError(err.message); }
        finally { setIsSaving(false); }
    };

    const activate = async (doc) => {
        if (!window.confirm(`Activate "${doc.title}" v${doc.version}? The current active version will be archived and staff will be asked to acknowledge this one.`)) return;
        try { await apiClient.post(`/api/policies/${doc.policy_id}/activate`); load(); }
        catch (err) { setError(err.message); }
    };

    // The AI Use Policy ships with starter text for this site's state. It arrives as a
    // draft, so the managing broker reads and activates it like any other version.
    const startFromStarter = async (docType) => {
        setError('');
        try { const r = await apiClient.post(`/api/policies/starter/${docType}`, {}); window.alert(r.message); load(); }
        catch (err) { setError(err.message); }
    };

    const deleteDraft = async (doc) => {
        if (!window.confirm(`Delete draft "${doc.title}" v${doc.version}?`)) return;
        try { await apiClient.delete(`/api/policies/${doc.policy_id}`); load(); }
        catch (err) { setError(err.message); }
    };

    const editDraft = async (doc) => {
        const full = await apiClient.get(`/api/policies/${doc.policy_id}`);
        setEditing({ policy_id: full.policy_id, doc_type: full.doc_type, title: full.title, content: full.content || '' });
    };

    const newVersionFrom = async (doc) => {
        const full = await apiClient.get(`/api/policies/${doc.policy_id}`);
        setEditing({ doc_type: full.doc_type, title: full.title, content: full.content || '' });
    };

    const viewAcks = async (doc) => {
        const rows = await apiClient.get(`/api/policies/${doc.policy_id}/acknowledgments`);
        setAcksFor({ doc, rows });
    };

    // Group by type; non-MB users see only active docs (API enforces too)
    const grouped = {};
    docs.forEach(d => { (grouped[d.doc_type] = grouped[d.doc_type] || []).push(d); });

    return (
        <div className="p-6 max-w-5xl">
            <div className="flex items-center justify-between mb-1">
                <h1 className="text-2xl font-bold text-slate-800">Policy Center</h1>
                {isMB && (
                    <button onClick={() => setEditing({ ...EMPTY_DRAFT })}
                            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700">+ New document</button>
                )}
            </div>
            <p className="text-slate-500 mb-6">The brokerage's governing documents — read them, acknowledge them, and (for the managing broker) keep them current.</p>

            {error && <p className="mb-4 text-red-500">{error}</p>}

            {pending.length > 0 && (
                <div className="mb-6 border border-amber-300 bg-amber-50 rounded-md p-4">
                    <p className="font-semibold text-amber-800 mb-2">⚠ You have {pending.length} document{pending.length > 1 ? 's' : ''} to acknowledge:</p>
                    {pending.map(p => (
                        <button key={p.policy_id} onClick={() => openDoc(p.policy_id)}
                                className="block text-amber-900 underline hover:text-amber-700">
                            {p.title} (v{p.version})
                        </button>
                    ))}
                </div>
            )}

            {isMB && !isLoading && !docs.some(d => d.doc_type === 'ai_use_policy') && (
                <div className="mb-6 border border-indigo-200 bg-indigo-50 rounded-md p-4 text-sm text-indigo-900">
                    There is no AI Use Policy yet.{' '}
                    <button onClick={() => startFromStarter('ai_use_policy')} className="underline font-medium">
                        Create a draft from the starter text for this state
                    </button>{' '}— then read it, edit if needed, and Activate.
                </div>
            )}

            {isLoading && <p>Opening the policy shelf...</p>}

            {Object.entries(grouped).map(([type, list]) => (
                <div key={type} className="mb-6">
                    <h2 className="text-lg font-semibold text-slate-700 mb-2">{TYPE_LABELS[type] || type}</h2>
                    <div className="bg-white rounded-lg shadow divide-y">
                        {list.map(doc => (
                            <div key={doc.policy_id} className="flex items-center gap-3 p-3">
                                <span className={`text-xs px-2 py-0.5 rounded ${STATUS_BADGE[doc.status]}`}>{doc.status}</span>
                                <button onClick={() => openDoc(doc.policy_id)} className="text-left flex-1 min-w-0">
                                    <p className="font-medium text-slate-700 truncate hover:underline">{doc.title} <span className="text-slate-400">v{doc.version}</span></p>
                                </button>
                                {doc.status === 'active' && doc.requires_acknowledgment === 1 && (
                                    doc.acknowledged_by_me
                                        ? <span className="text-xs text-green-600">✓ acknowledged</span>
                                        : <span className="text-xs text-amber-600">signature needed</span>
                                )}
                                {isMB && (
                                    <span className="flex gap-2 text-sm">
                                        {doc.status === 'active' && <button onClick={() => viewAcks(doc)} className="text-blue-600 hover:underline">{doc.acknowledgment_count} signed</button>}
                                        {doc.status === 'draft' && <>
                                            <button onClick={() => editDraft(doc)} className="text-blue-600 hover:underline">Edit</button>
                                            <button onClick={() => activate(doc)} className="text-green-600 hover:underline">Activate</button>
                                            <button onClick={() => deleteDraft(doc)} className="text-red-500 hover:underline">Delete</button>
                                        </>}
                                        {doc.status !== 'draft' && <button onClick={() => newVersionFrom(doc)} className="text-slate-500 hover:underline">New version</button>}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ))}

            {/* Read + acknowledge modal */}
            <Modal open={!!reading} onClose={() => setReading(null)} title={reading ? `${reading.title} (v${reading.version})` : ''} size="lg"
                   footer={reading && reading.status === 'active' && reading.requires_acknowledgment === 1 && !docs.find(d => d.policy_id === reading.policy_id)?.acknowledged_by_me ? (
                       <>
                           <input value={signature} onChange={e => setSignature(e.target.value)}
                                  placeholder="Type your full legal name"
                                  className="border rounded-md px-3 py-2 flex-1" />
                           <button onClick={acknowledge} disabled={isSaving || !signature.trim()}
                                   className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50">
                               {isSaving ? 'Signing...' : 'I have read and agree'}
                           </button>
                       </>
                   ) : null}>
                {reading && <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: reading.content }} />}
            </Modal>

            {/* Draft editor modal (MB) */}
            <Modal open={!!editing} onClose={() => setEditing(null)}
                   title={editing?.policy_id ? 'Edit draft' : 'New document draft'} size="lg"
                   footer={<>
                       <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-md border">Cancel</button>
                       <button onClick={saveDraft} disabled={isSaving || !editing?.title?.trim()}
                               className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50">
                           {isSaving ? 'Saving...' : 'Save draft'}
                       </button>
                   </>}>
                {editing && (
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-medium text-slate-700">Document type</label>
                                <select value={editing.doc_type} disabled={!!editing.policy_id}
                                        onChange={e => setEditing(prev => ({ ...prev, doc_type: e.target.value }))}
                                        className="mt-1 w-full border rounded-md px-3 py-2">
                                    {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700">Title</label>
                                <input value={editing.title} onChange={e => setEditing(prev => ({ ...prev, title: e.target.value }))}
                                       className="mt-1 w-full border rounded-md px-3 py-2" />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700">Content (HTML)</label>
                            <textarea value={editing.content} rows="18"
                                      onChange={e => setEditing(prev => ({ ...prev, content: e.target.value }))}
                                      className="mt-1 w-full border rounded-md px-3 py-2 font-mono text-xs" />
                        </div>
                        <p className="text-xs text-slate-500">Saving creates a draft — nothing is published until you Activate it. Activating archives the prior version and asks all staff to acknowledge the new one.</p>
                    </div>
                )}
            </Modal>

            {/* Acknowledgments modal (MB) */}
            <Modal open={!!acksFor} onClose={() => setAcksFor(null)}
                   title={acksFor ? `Acknowledgments — ${acksFor.doc.title} v${acksFor.doc.version}` : ''}>
                {acksFor && (acksFor.rows.length === 0
                    ? <p className="text-slate-500">Nobody has signed this version yet.</p>
                    : <div className="divide-y">
                        {acksFor.rows.map(r => (
                            <div key={r.ack_id} className="py-2 flex justify-between text-sm">
                                <span className="font-medium text-slate-700">{r.signature_name} <span className="text-slate-400">({r.username})</span></span>
                                <span className="text-slate-500">{new Date(r.acknowledged_at).toLocaleString()}</span>
                            </div>
                        ))}
                      </div>)}
            </Modal>
        </div>
    );
};

export default PolicyCenterPage;
